import { useEffect, useRef } from 'react';
import {
  useFormContext,
  useWatch,
  type FieldValues,
  type Path,
  type PathValue,
  type UseFormGetValues,
} from 'react-hook-form';

const SET_OPTIONS = {
  shouldDirty: true,
  shouldValidate: true,
  shouldTouch: true,
} as const;

interface UseUnlimitedToggleParams<T extends FieldValues> {
  valueName: Path<T>;
  toggleName: Path<T>;
  getFallback: (lastValue: string, getValues: UseFormGetValues<T>) => string;
}

interface UnlimitedToggle {
  isUnlimited: boolean;
  toggle: (next: boolean) => void;
}

export function useUnlimitedToggle<T extends FieldValues>({
  valueName,
  toggleName,
  getFallback,
}: UseUnlimitedToggleParams<T>): UnlimitedToggle {
  const { control, getValues, setValue, trigger } = useFormContext<T>();
  const isUnlimited = Boolean(useWatch({ control, name: toggleName }));
  const value = useWatch({ control, name: valueName }) as string;

  const lastValue = useRef('');
  useEffect(() => {
    if (!isUnlimited && value) {
      lastValue.current = value;
    }
  }, [isUnlimited, value]);

  const toggle = (next: boolean): void => {
    setValue(toggleName, next as PathValue<T, Path<T>>, SET_OPTIONS);
    if (!next) {
      setValue(
        valueName,
        getFallback(lastValue.current, getValues) as PathValue<T, Path<T>>,
        SET_OPTIONS
      );
    }
    void trigger(valueName);
  };

  return { isUnlimited, toggle };
}
